import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

export function Login() {
  const { login, loginWithGoogle } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await login(email, password);
      toast.success(`Bentornato, ${user.name}!`);
      const from = location.state?.from;
      navigate(from || (user.role === "professional" ? "/pro" : "/home"), { replace: true });
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="px-4 pt-10 sm:pt-16 space-y-8 sm:max-w-md sm:mx-auto" data-testid="login-page">
      <div className="space-y-2">
        <h1 className="font-heading text-3xl font-bold tracking-tight text-slate-900">Accedi</h1>
        <p className="text-base text-slate-600">Usa lo stesso account dell'app WhooNow.</p>
      </div>

      <form onSubmit={submit} className="space-y-4" data-testid="login-form">
        <div className="space-y-2">
          <Label htmlFor="email">Email</Label>
          <Input id="email" data-testid="login-email-input" type="email" required autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)}
            className="h-14 bg-slate-50 border-transparent focus:bg-white focus:border-volt rounded-2xl" placeholder="nome@email.it" />
        </div>
        <div className="space-y-2">
          <Label htmlFor="password">Password</Label>
          <Input id="password" data-testid="login-password-input" type="password" required autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)}
            className="h-14 bg-slate-50 border-transparent focus:bg-white focus:border-volt rounded-2xl" placeholder="La tua password" />
        </div>
        <Button data-testid="login-submit-btn" type="submit" disabled={loading}
          className="w-full h-14 rounded-full bg-whoo hover:bg-whoo-dark text-white font-heading font-semibold text-base active:scale-[0.98] transition-transform">
          {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Accedi"}
        </Button>
      </form>

      <div className="flex items-center gap-3">
        <div className="h-px flex-1 bg-slate-200" />
        <span className="text-xs font-semibold text-slate-400 uppercase">oppure</span>
        <div className="h-px flex-1 bg-slate-200" />
      </div>

      <Button data-testid="google-login-btn" variant="outline" onClick={loginWithGoogle}
        className="w-full h-14 rounded-full font-semibold active:scale-[0.98] transition-transform">
        <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.1a7.2 7.2 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z"/></svg>
        Continua con Google
      </Button>

      <div className="text-center space-y-2 pb-6">
        <Link to="/reset-password" data-testid="forgot-password-link" className="text-sm text-volt font-semibold">Password dimenticata?</Link>
        <p className="text-sm text-slate-600">
          Non hai un account? <Link to="/register" data-testid="go-register-link" className="text-whoo font-semibold">Registrati</Link>
        </p>
      </div>
    </div>
  );
}
